package com.oasisfeng.invaluable.identity.store

import com.oasisfeng.invaluable.identity.Identity

private const val PREFIX = "invaluable"
private const val FILE_NAME = "identity.key"

/**
 * JS persistence helper for [Identity].
 *
 * - Browser: stores key material in `localStorage` under a per-namespace key.
 * - Node.js: stores key material in `<dirPath>/identity.key` for TUI runs.
 */
fun Identity.Companion.loadOrCreate(namespace: String): Identity {
    if (!isBrowser()) return loadOrCreateInNodeFile(namespace)
    val key = identityStorageKey(namespace)
    val stored = BrowserStore.getItem(key)
    return if (stored != null) deserialize(stored) else generate().also { BrowserStore.setItem(key, serialize(it)) }
}

/** The [BrowserStore] key under which [loadOrCreate] persists the browser identity for [namespace]. */
fun identityStorageKey(namespace: String): String = "$PREFIX.$namespace.identity"

// Detects a browser by the presence of `window` only — deliberately *not* touching `localStorage`, whose mere access
// throws on `file://` in Safari (so the previous `typeof window.localStorage` guard could itself crash). Usability of
// storage is handled downstream by [BrowserStore], which falls back to memory when access throws.
private fun isBrowser(): Boolean =
    js("typeof globalThis !== 'undefined' && typeof globalThis.window !== 'undefined'") as Boolean

private fun Identity.Companion.loadOrCreateInNodeFile(dirPath: String): Identity {
    val fs = js("eval('require')('fs')")
    if (!(fs.existsSync(dirPath) as Boolean)) fs.mkdirSync(dirPath, js("{ recursive: true }"))
    val filePath = "$dirPath/$FILE_NAME"
    return if (fs.existsSync(filePath) as Boolean) {
        deserialize(fs.readFileSync(filePath, "utf-8") as String)
    } else {
        generate().also { fs.writeFileSync(filePath, serialize(it)) }
    }
}
